import React, { useEffect, useRef, useState } from 'react';
import { createChart, CrosshairMode, LineStyle } from 'lightweight-charts';
import PageServices from '../../services/PageServices';

// Spot Chart popup on the option chain page (AOC style): today's spot candles of the
// selected interval (GET /spotCandles) with resistance / support lines at the reversal
// values of the strikes around spot (each level: { kind, strike, price }). Candles refresh
// every REFRESH_MS while the popup is open; the levels follow the chain passed in by the page.

const REFRESH_MS = 10_000;
const IST_OFFSET_SEC = 330 * 60; // lightweight-charts shows UTC; shift so the axis reads IST
const GREEN = '#4caf50';
const RED = '#e53935';

export const SPOT_CHART_MODAL_ID = 'spotChartModal';

const INTERVALS = [1, 3, 5, 15]; // minutes

const fmtStrike = (k) => Number(k).toLocaleString('en-IN');

// `date` / `time` (optional, Backtester): candles of that day up to that time, loaded once.
// `modalId` lets a second page use its own popup id.
const SpotChartModal = ({ symbol, levels = [], date, time, modalId = SPOT_CHART_MODAL_ID }) => {
  const modalRef = useRef(null);
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const priceLinesRef = useRef([]);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('loading'); // loading | ready | empty
  const [lastPrice, setLastPrice] = useState(null);
  const [interval, setIntervalMinutes] = useState(1);

  // Follow the Bootstrap modal so data is fetched only while it is open
  useEffect(() => {
    const modal = modalRef.current;
    if (!modal) return undefined;
    const onShow = () => setOpen(true);
    const onHide = () => setOpen(false);
    modal.addEventListener('show.bs.modal', onShow);
    modal.addEventListener('hidden.bs.modal', onHide);
    return () => {
      modal.removeEventListener('show.bs.modal', onShow);
      modal.removeEventListener('hidden.bs.modal', onHide);
    };
  }, []);

  // Chart is created once and resizes with the popup
  useEffect(() => {
    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: { background: { color: '#131313' }, textColor: '#e8e8e8', fontSize: 13 },
      grid: { vertLines: { color: '#262626' }, horzLines: { color: '#262626' } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: '#2a2a2a' },
      timeScale: { borderColor: '#2a2a2a', timeVisible: true, secondsVisible: false },
    });
    seriesRef.current = chart.addCandlestickSeries({
      upColor: GREEN, downColor: RED, borderUpColor: GREEN, borderDownColor: RED,
      wickUpColor: GREEN, wickDownColor: RED,
    });
    chartRef.current = chart;
    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      priceLinesRef.current = [];
    };
  }, []);

  // Watermark with the symbol
  useEffect(() => {
    chartRef.current?.applyOptions({
      watermark: { visible: !!symbol, text: symbol || '', color: 'rgba(255,255,255,0.06)', fontSize: 64 },
    });
  }, [symbol]);

  // Load now and every REFRESH_MS while open; a new symbol or interval starts from a fresh chart
  useEffect(() => {
    if (!open || !symbol) return undefined;
    let cancelled = false;
    let first = true;
    const load = () => {
      PageServices.getSpotCandles(symbol, interval, date, time)
        .then((response) => {
          if (cancelled || !seriesRef.current) return;
          const candles = (response?.data?.candles || []).map((c) => ({ ...c, time: c.time + IST_OFFSET_SEC }));
          seriesRef.current.setData(candles);
          setStatus(candles.length ? 'ready' : 'empty');
          setLastPrice(candles.length ? candles[candles.length - 1].close : null);
          if (first && candles.length) chartRef.current?.timeScale().fitContent();
          first = false;
        })
        .catch(() => {
          if (!cancelled && first) setStatus('empty');
        });
    };
    setStatus('loading');
    load();
    const id = date ? null : setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      if (id) clearInterval(id);
    };
  }, [open, symbol, interval, date, time]);

  // Resistance / support lines follow the levels passed in by the page
  const levelsKey = levels.map((l) => `${l.kind}${l.strike}:${l.price}`).join(',');
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    priceLinesRef.current.forEach((line) => series.removePriceLine(line));
    priceLinesRef.current = levels.map((l) => series.createPriceLine({
      price: l.price,
      color: l.kind === 'R' ? RED : GREEN,
      lineWidth: 2,
      lineStyle: LineStyle.Solid,
      axisLabelVisible: true,
      title: `${l.kind} ${fmtStrike(l.strike ?? l.price)}`,
    }));
  }, [levelsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={modalRef}
      className="modal fade"
      id={modalId}
      tabIndex={-1}
      aria-labelledby={`${modalId}Label`}
      aria-hidden="true"
    >
      <div className="modal-dialog ai-chart modal-dialog-centered">
        <div className="modal-content modal-bg-light-blue oi-charts-dark">
          <div className="modal-header">
            <h5 className="modal-title" id={`${modalId}Label`}>
              {symbol} Spot{lastPrice != null ? ` · ${lastPrice.toFixed(2)}` : ''}
            </h5>
            <div className="btn-group btn-group-sm ms-auto me-3" role="group" aria-label="Candle interval">
              {INTERVALS.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`btn ${interval === m ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setIntervalMinutes(m)}
                >
                  {m} min
                </button>
              ))}
            </div>
            <button type="button" className="btn-close btn-close-white ms-0" data-bs-dismiss="modal" aria-label="Close" />
          </div>
          <div style={{ position: 'relative', height: 560, background: '#131313' }}>
            <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
            {status !== 'ready' && (
              <p style={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: 0, color: '#8a8a8a', pointerEvents: 'none',
              }}>
                {status === 'loading' ? 'Loading…' : 'No data available'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpotChartModal;
